import { Div } from '@codesuma/baseline'
import settings, { CalendarSystem } from '../../../services/settings'
import { monthBackgroundX } from '../../../configs/background'
import { monthColor } from '../../../configs/colors'
import theme from '../../../services/theme'
import style from './index.module.css'

// The seasonal panorama behind the month slides — one continuous layer, not a
// piece per month. The months pan across it, so no two slides meet on a cut.
//
// It follows a swipe at half the slides' speed, which is the parallax: a
// committed swipe carries the slides a full screen and the panorama half of
// one, and half a screen is exactly the twelfth of the image that separates
// two months. So the pan lands on the next month's frame with nothing to snap.
//
// A theme change simply swaps the art. The dissolve between day and night is
// the theme service's, which cross-fades the whole screen — see services/theme.
export const MonthBackdrop = () => {
    const base = Div()
    base.addClass(style.base)

    const layer = Div()
    layer.addClass(style.layer)
    base.append(layer)

    let restX = '100%' // the frame the current month rests on
    // Which way a swipe carries the months. Gregorian runs LTR (see .mirrored).
    let sign: 1 | -1 = 1

    const paint = (x: string, animate: boolean) => {
        layer.toggleClass(style.settling, animate)
        layer.style({ backgroundPositionX: x })
    }

    // The theme's art — or no art at all, which is what the seasons theme is:
    // the month's own color, left to show through on its own. Nothing to wait
    // for; the theme service decoded the image before this ran.
    const show = () => {
        const art = theme.panorama()
        layer.style({ backgroundImage: art ? `url("${art}")` : 'none' })
    }

    show()

    return Object.assign(base, {
        // Come to rest on a month's frame.
        month(month: number, system: CalendarSystem) {
            sign = system === 'gregorian' ? -1 : 1
            base.toggleClass(style.mirrored, system === 'gregorian')
            restX = monthBackgroundX(month, system)
            paint(restX, false)
            // In the seasons theme there is no art to pan — the month's own
            // color IS the paper, and the whole shell follows it so a route
            // transition never flashes something else.
            if (settings.get().theme === 'seasons') theme.setPaper(monthColor(month, system))
        },
        // Follow a swipe of dx px, over ms (0 while the finger is still down).
        drag(dx: number, ms: number) {
            paint(`calc(${restX} + ${sign * dx / 2}px)`, ms > 0)
        },
        theme: show,
    })
}
